import { AttributesMixin, define, Model, type } from "@type-r/models";
import { pollWithLimit } from "../shared";
import { restfulIO } from "./endpoint";

export enum AssetEventType {
    send_inmatch = 1,
    inblock_mapper = 3,
    scf_ingestor = 9,
    pts_ingestor = 15,
    abf_ingestor = 19,
    ingested_pts = 22,
    received_inmatch = 23,
    ingested_inmatch = 24
}

export enum AssetEventStatus {
    pending,
    received
}

export enum AssetEventProcessStatus {
    undefined = 0,
    processing = 1,
    finished = 2
}

export enum GenericExternalSystemType {
    inblock = 1,
    scf = 2,
    abf = 3,
    pts = 4
}

export interface AssetEvent extends AttributesMixin<typeof AssetEvent> { }
@define
export class AssetEvent extends Model {
    static endpoint = restfulIO('/api/v1/assetmaster/assetsEvents');

    static attributes = {
        id: String,
        uid: String,
        assetId: Number,
        assetTypeId: Number,
        eventTypeId: Number,
        success: Boolean,
        message: String,
        createdOn: Date,
        createdBy: String,
        metadata: String,
        initiatedBy: String,
        status: type(Number).value(AssetEventStatus.pending).dontSave
    };

    get isPending() {
        return this.status == AssetEventStatus.pending;
    }

    get hasFailed() {
        return this.status == AssetEventStatus.received && !this.success;
    }

    get hasSucceeded() {
        return this.status == AssetEventStatus.received && this.success;
    }
}

export interface AssetEventLatest extends AttributesMixin<typeof AssetEventLatest> { }
@define
export class AssetEventLatest extends Model {
    static endpoint = restfulIO('/api/v1/assetmaster/assetsEvents/latest');

    static attributes = {
        eventTypeId: Number,
        assetEventStatus: Number,
        assetUpdated: Boolean,
        event: String,
        externalSystemType: Number,
        createdOn: String,
        success: Boolean,
        message: String
    };
}

const getAssetsWithoutReceivedEvents = assets => {
    //console.log("getAssetsWithoutReceivedEvents with assets.length:" + assets.length);
    const result = assets.models.filter(a =>
        !a.assetEvents ||
        a.assetEvents.length <= 0 ||
        a.assetEvents.models.filter(ae => ae.status != AssetEventStatus.received).length > 0);
    return result;
};

const getAssetUidsAll = (assets, batchSize, useNumericIds = false) => {
    const assetsUids = assets.models
        .filter(assetChange => assetChange.assetId != "00000000-0000-0000-0000-000000000000")
        .slice(0, batchSize)
        .map(assetChange => {
            const dynamic: any = assetChange;
            
            if (useNumericIds) {
                const numericId = dynamic.id || (dynamic.changes && dynamic.changes.assetId) || (dynamic.asset && dynamic.asset.assetId);
                if (numericId && typeof numericId === 'number' && numericId > 0) {
                    return numericId.toString();
                }
                return null;
            }
            
            const uuid = assetChange.assetId;
            const idFromChanges = dynamic.changes && dynamic.changes.id;
            const idFromAsset = dynamic.asset && dynamic.asset.id;
            const finalUuid = uuid || idFromChanges || idFromAsset;
            
            if (!finalUuid || finalUuid === "00000000-0000-0000-0000-000000000000") {
                return null;
            }
            
            return finalUuid;
        })
        .filter(id => id !== null);

    return assetsUids;
};

const getAssetUids = (assets, batchSize, useNumericIds = false) => {
    const assetsUids = getAssetsWithoutReceivedEvents(assets)
        .filter(assetChange => assetChange.assetId != "00000000-0000-0000-0000-000000000000")
        .sort((first, second) => first.assetEvents && first.assetEvents.length > 0 ? -1 : (second.assetEvents && second.assetEvents.length > 0 ? 1 : 0))
        .slice(0, batchSize)
        .map(assetChange => {
            const dynamic: any = assetChange;
            
            if (useNumericIds) {
                const numericId = dynamic.id || (dynamic.changes && dynamic.changes.assetId) || (dynamic.asset && dynamic.asset.assetId);
                if (numericId && typeof numericId === 'number' && numericId > 0) {
                    return numericId.toString();
                }
                return null;
            }
            
            return assetChange.assetId;
        })
        .filter(id => id !== null);

    return assetsUids;
};

const fetchEvents = async (assets, targetCollection, batchSize = 500, importJobId?: number, fetchAllAssets = false) => {
    const json = await fetchEventsBase(assets, targetCollection, batchSize, importJobId, false, fetchAllAssets);
    if (json) {
        targetCollection.set(json);
    }
    return assets;
};

export const fetchIncomingAssetEvents = async (assets, incomingAssetEvents) => {
    //console.log("fetchIncomingAssetEvents with assets.length:" + assets.length);
    const json = await fetchEventsBase(assets, incomingAssetEvents);

    incomingAssetEvents.transaction(() => {
        json && json.forEach(jEvent => {
            incomingAssetEvents.add(jEvent);
        });
    });
};

export const fetchEventsBase = async (assets, incomingAssetEvents, batchSize?, importJobId?: number, tryNumericIds = false, fetchAllAssets = false) => {
    const assetsUids = fetchAllAssets 
        ? getAssetUidsAll(assets, batchSize, tryNumericIds)
        : getAssetUids(assets, batchSize, tryNumericIds);

    if (assetsUids.length <= 0) {
        return;
    }

    const endpoint = incomingAssetEvents.getEndpoint();
    let url = endpoint.url;
    if (importJobId !== undefined && importJobId !== null) {
        const separator = url.includes('?') ? '&' : '?';
        url += `${separator}importJobId=${importJobId}`;
    }
    let body = JSON.stringify(assetsUids);

    const response = await fetch(url, {
        method: 'POST',
        body: body,
        headers: new Headers({
            "Authorization": "Bearer " + endpoint.tokenProvider(),
            "Content-Type": "application/json"
        }),
    });

    if (!response.ok) {
        const errorText = await response.text();
        console.error('fetchEventsBase: API error:', response.status, response.statusText, errorText, 'Request body:', body);
        return;
    }

    const json = await response.json();
    if (!json || json.length <= 0) {
        return;
    }
    return json;
};

export const pollAssetEvents = async (assets, incomingAssetEvents, batchSize?) => {
    //console.log("pollAssetEvents with assets.length:" + assets.length);
    if (assets.length > 0) {
        const shouldContinuePolling = () => getAssetsWithoutReceivedEvents(assets).length > 0;
        await pollWithLimit(async () => await fetchEvents(assets, incomingAssetEvents, batchSize), shouldContinuePolling, 5000, 100);
    }
};

export const getAssetEvents = async (assetChanges, targetCollection, batchSize?, importJobId?: number, fetchAllAssets = false) => {
    if (assetChanges.length > 0) {
        await fetchEvents(assetChanges, targetCollection, batchSize, importJobId, fetchAllAssets)
    }
};
